"use client";

import { BigMath } from "@yn/utils";
import { normalizeLimitPrice } from "../utils/normalizeLimitPrice";
import type {
    BookProfitStopLossDraft,
    BookProfitStopLossKind,
    BookProfitStopLossLegDraft,
    BookProfitStopLossLegValidation,
    BookProfitStopLossValidationResult,
} from "./types";

export const BOOK_PROFIT_HELPER_TEXT =
    "A sell order will be placed at your set profit price once the market price reaches your trigger level and your buy order is fully matched.";
export const STOP_LOSS_HELPER_TEXT =
    "A sell order will be placed at your stop-loss price once the market price reaches your trigger level and your buy order is fully matched.";
export const BOOK_PROFIT_STOP_LOSS_PRICE_STEP = 1;
// 价格编辑的上下界要与文案里描述的可用性阈值保持一致。
export const BOOK_PROFIT_PRICE_MAX = 99;
export const STOP_LOSS_PRICE_MIN = 1;

const EMPTY_LEG: BookProfitStopLossLegDraft = { enabled: false, price: "", quantity: "" };

/**
 * 生成阈值失效时的价格来源文案。
 *
 * @param labels 触发阈值的价格来源。
 * @returns 返回可直接插入英文句子的来源描述。
 */
function formatBlockedPriceLabels( labels: string[] ) {
    const uniqueLabels = [ ...new Set( labels ) ];

    if ( uniqueLabels.length <= 1 ) {
        return uniqueLabels[0] ?? "trade price";
    }

    return `${uniqueLabels.slice( 0, -1 ).join( ", " )} or ${uniqueLabels[uniqueLabels.length - 1]}`;
}

/**
 * 生成单条 leg 因价格阈值不可用时的说明文案。
 *
 * @param kind 当前校验的是止盈还是止损。
 * @param blockedLabels 触发阈值的价格来源。
 * @returns 返回阈值不可用文案。
 */
function createThresholdDisabledText( kind: BookProfitStopLossKind, blockedLabels: string[] ) {
    const priceLabelsText = formatBlockedPriceLabels( blockedLabels );
    const isEntryPriceText = priceLabelsText === "Edit Price" || priceLabelsText === "buy price";

    return kind === "bookProfit"
        ? `Book profit can not be set when ${
            isEntryPriceText ? "trade price" : priceLabelsText
        } is ${BOOK_PROFIT_PRICE_MAX}${isEntryPriceText ? "" : " or more"}`
        : isEntryPriceText
        ? `Stop Loss cannot be enabled when the entry price is ${STOP_LOSS_PRICE_MIN}`
        : `Stop loss can not be set when ${priceLabelsText} is ${STOP_LOSS_PRICE_MIN} or less`;
}

/**
 * 生成单条 leg 因全局价格边界没有合法档位时的说明文案。
 *
 * @param kind 当前校验的是止盈还是止损。
 * @param priceReferenceLabel 当前 leg 的价格参考来源。
 * @returns 返回无合法价格区间时的说明文案。
 */
function createRangeUnavailableText( kind: BookProfitStopLossKind, priceReferenceLabel: string ) {
    return kind === "bookProfit"
        ? `Book profit can not be set because ${priceReferenceLabel} is too close to ${BOOK_PROFIT_PRICE_MAX}`
        : `Stop loss can not be set because ${priceReferenceLabel} is too close to ${STOP_LOSS_PRICE_MIN}`;
}

/**
 * 判断当前参考价下是否还存在合法 BP/SL 价格档位。
 *
 * @param kind 当前校验的是止盈还是止损。
 * @param priceReference 当前 leg 的价格参考价。
 * @returns 存在合法档位时返回 `true`。
 */
function hasAvailablePriceRange( kind: BookProfitStopLossKind, priceReference: number ) {
    if ( !Number.isFinite( priceReference ) ) return false;

    const { min, max } = getBookProfitStopLossPriceRange( kind, priceReference );

    return max >= min;
}

/**
 * 校验数量是否落在当前允许范围内。
 *
 * @param value 当前草稿中的数量值，解析失败时为 `null`。
 * @param maxQuantity 当前界面允许提交的最大数量。
 * @returns 返回数量错误文案；数量合法时返回 `null`。
 */
function quantityLimitError( value: number | null, maxQuantity: number ) {
    if ( value === null ) return "Enter a quantity";
    if ( BigMath.lte( value, 0 ) ) return "Quantity must be greater than 0";
    if ( BigMath.gt( value, maxQuantity ) ) return `Quantity can not exceed ${maxQuantity}`;
    return null;
}

/**
 * 生成单条 leg 的价格错误信息。
 *
 * @param params.kind 当前校验的是止盈还是止损。
 * @param params.enabled 当前 leg 是否处于开启状态。
 * @param params.price 当前价格输入解析后的数值；未填或非法时为 `null`。
 * @param params.priceReference 当前价格规则的参考价，用来约束止盈/止损方向。
 * @returns 返回价格错误文案；价格合法时返回 `null`。
 */
function createPriceError(
    params: {
        kind: BookProfitStopLossKind;
        enabled: boolean;
        price: number | null;
        priceReference: number;
        priceReferenceLabel?: string;
    },
) {
    const { kind, enabled, price, priceReference, priceReferenceLabel = "buy price" } = params;
    if ( !enabled ) return null;
    if ( price === null ) {
        return kind === "bookProfit" ? "Enter a book profit price" : "Enter a stop loss price";
    }
    if ( kind === "bookProfit" && BigMath.gt( price, BOOK_PROFIT_PRICE_MAX ) ) {
        return `Book profit price can not be greater than ${BOOK_PROFIT_PRICE_MAX}`;
    }
    if ( kind === "stopLoss" && BigMath.lt( price, STOP_LOSS_PRICE_MIN ) ) {
        return `Stop loss price can not be lower than ${STOP_LOSS_PRICE_MIN}`;
    }
    if ( kind === "bookProfit" && BigMath.lte( price, priceReference ) ) {
        return `Book profit price must be greater than the ${priceReferenceLabel}`;
    }
    if ( kind === "stopLoss" && BigMath.gte( price, priceReference ) ) {
        return `Stop loss price must be lower than the ${priceReferenceLabel}`;
    }
    const normalizedPrice = clampBookProfitStopLossPrice( kind, price, priceReference );
    if ( normalizedPrice === null ) {
        return kind === "bookProfit"
            ? `Book profit price must be at least ${BOOK_PROFIT_STOP_LOSS_PRICE_STEP} cent above the ${priceReferenceLabel}`
            : `Stop loss price must be at least ${BOOK_PROFIT_STOP_LOSS_PRICE_STEP} cent below the ${priceReferenceLabel}`;
    }
    if ( !BigMath.eq( normalizedPrice, price ) ) {
        return kind === "bookProfit"
            ? `Book profit price must move in ${BOOK_PROFIT_STOP_LOSS_PRICE_STEP}-cent increments from the ${priceReferenceLabel}`
            : `Stop loss price must move in ${BOOK_PROFIT_STOP_LOSS_PRICE_STEP}-cent increments from the ${priceReferenceLabel}`;
    }
    return null;
}

/**
 * 创建一份全新的空 BP/SL 草稿。
 *
 * @returns 返回未开启、价格和数量都为空的初始草稿。
 */
export function createEmptyBookProfitStopLossDraft(): BookProfitStopLossDraft {
    return { bookProfit: { ...EMPTY_LEG }, stopLoss: { ...EMPTY_LEG } };
}

/**
 * 深拷贝一份 BP/SL 草稿。
 *
 * @param draft 要复制的草稿；为空时会退回到空草稿。
 * @returns 返回一份新的草稿对象，避免多个界面共享引用。
 */
export function cloneBookProfitStopLossDraft(
    draft: BookProfitStopLossDraft | null | undefined,
): BookProfitStopLossDraft {
    const safeDraft = draft ?? createEmptyBookProfitStopLossDraft();

    return { bookProfit: { ...safeDraft.bookProfit }, stopLoss: { ...safeDraft.stopLoss } };
}

/**
 * 清洗通用数字输入。
 *
 * @param rawValue 输入框中的原始字符串。
 * @returns 仅保留数字和一位小数的字符串结果。
 */
export function sanitizeNumericInput( rawValue: string ) {
    const normalized = rawValue.replace( /[^\d.]/g, "" );
    const [ intPart = "", ...restParts ] = normalized.split( "." );
    const decimalPart = restParts.join( "" ).slice( 0, 1 );
    const safeIntPart = intPart.replace( /^0+(?=\d)/, "" );

    return decimalPart ? `${safeIntPart || "0"}.${decimalPart}` : safeIntPart;
}

/**
 * 清洗价格输入，并保留输入过程中的中间态。
 *
 * @param rawValue 输入框中的原始字符串。
 * @returns 返回只保留整数的价格字符串。
 */
export function sanitizePriceInput( rawValue: string ) {
    const [ integerPart = "" ] = rawValue.replace( /[^\d.]/g, "" ).split( "." );
    return integerPart.replace( /^0+(?=\d)/, "" );
}

/**
 * 把草稿中的字符串数值解析成真正参与比较的数字。
 *
 * @param value 草稿中的价格或数量字符串。
 * @returns 可用数字时返回数字；空值或非法值时返回 `null`。
 */
export function parseBookProfitStopLossNumber( value: string ) {
    if ( !value ) return null;
    const parsed = Number( value );
    return Number.isFinite( parsed ) ? parsed : null;
}

/**
 * 汇总单条 leg 的校验结果，生成界面可直接消费的 validation 结构。
 *
 * @param draft 当前 leg 的草稿数据。
 * @param helperText 当前状态下应该展示的说明文案。
 * @param available 当前 leg 是否允许开启。
 * @param shouldAutoDisable 当前 leg 是否需要被系统自动关闭。
 * @param quantityError 当前数量错误文案。
 * @param priceError 当前价格错误文案。
 * @returns 返回单条 leg 的完整校验结果。
 */
function createValidation(
    draft: BookProfitStopLossLegDraft,
    helperText: string,
    available: boolean,
    shouldAutoDisable: boolean,
    quantityError: string | null,
    priceError: string | null,
): BookProfitStopLossLegValidation {
    const isValid = !draft.enabled || ( available && !quantityError && !priceError );

    return { available, helperText, quantityError, priceError, shouldAutoDisable, isValid };
}

/**
 * 生成打开 BP/SL 开关时的默认建议价。
 *
 * @param kind 当前生成的是止盈还是止损价格。
 * @param priceReference 当前价格规则的参考价。
 * @returns 返回距离参考价最近、且仍然合法的一档价格字符串。
 */
export function createSuggestedPrice( kind: BookProfitStopLossKind, priceReference: number ) {
    // 默认给参考价附近最近的合法档位，这样打开开关后就有一个可编辑的起始值，
    // 同时不会擅自给出更大的默认价差。
    const rawValue = kind === "bookProfit"
        ? Number( BigMath.plus( priceReference, BOOK_PROFIT_STOP_LOSS_PRICE_STEP ) )
        : Number( BigMath.minus( priceReference, BOOK_PROFIT_STOP_LOSS_PRICE_STEP ) );
    const bounded = clampBookProfitStopLossPrice( kind, rawValue, priceReference )
        ?? Math.min( BOOK_PROFIT_PRICE_MAX, Math.max( STOP_LOSS_PRICE_MIN, rawValue ) );

    return String( bounded );
}

/**
 * 计算当前参考价对应的 BP/SL 可编辑价格区间。
 *
 * @param kind 当前价格对应的 leg 类型。
 * @param priceReference 当前价格规则的参考价。
 * @returns 返回当前允许的最小值和最大值。
 */
export function getBookProfitStopLossPriceRange( kind: BookProfitStopLossKind, priceReference: number ) {
    return kind === "bookProfit"
        ? {
            min: BigMath.ceil( BigMath.plus( priceReference, BOOK_PROFIT_STOP_LOSS_PRICE_STEP ) ),
            max: BOOK_PROFIT_PRICE_MAX,
        }
        : {
            min: STOP_LOSS_PRICE_MIN,
            max: BigMath.floor( BigMath.minus( priceReference, BOOK_PROFIT_STOP_LOSS_PRICE_STEP ) ),
        };
}

/**
 * 按当前参考价和全局边界裁剪 BP/SL 价格。
 *
 * @param kind 当前价格对应的 leg 类型。
 * @param nextValue 待裁剪的候选价格。
 * @param priceReference 当前价格规则的参考价。
 * @returns 返回裁剪后的合法价格；无合法结果时返回 `null`。
 */
export function clampBookProfitStopLossPrice(
    kind: BookProfitStopLossKind,
    nextValue: number,
    priceReference: number,
) {
    if ( !Number.isFinite( nextValue ) || !Number.isFinite( priceReference ) ) return null;

    const { min, max } = getBookProfitStopLossPriceRange( kind, priceReference );

    if ( max < min ) return null;

    const normalizedValue = normalizeLimitPrice( nextValue );

    if ( normalizedValue <= 0 ) return null;

    return Math.min( max, Math.max( min, normalizedValue ) );
}

/**
 * 判断草稿中是否存在至少一个已开启的 BP/SL 配置。
 *
 * @param draft 当前 BP/SL 草稿。
 * @returns 只要止盈或止损任意一个开启，就返回 `true`。
 */
export function hasBookProfitStopLossConfigured( draft: BookProfitStopLossDraft ) {
    return draft.bookProfit.enabled || draft.stopLoss.enabled;
}

/**
 * 基于当前价格源、购买价和数量上限，生成整份草稿的校验结果。
 *
 * @param params.draft 当前 BP/SL 草稿。
 * @param params.tradePrice 当前市场价，只用于判断 BP/SL 开关临界值。
 * @param params.tradePriceLabel 当前市场价的展示名称。
 * @param params.buyPrice 当前购买价；未显式传 `priceReference` 时作为价格设置参考价。
 * @param params.priceReference 显式价格设置参考价。
 * @param params.maxQuantity 当前可提交的最大数量。
 * @returns 返回整份草稿的校验信息，包含每个 leg 的错误态和整体验证结果。
 */
export function validateBookProfitStopLossDraft(
    params: {
        draft: BookProfitStopLossDraft;
        tradePrice: number;
        tradePriceLabel?: string;
        buyPrice?: number;
        priceReference?: number;
        maxQuantity: number;
        priceReferenceLabel?: string;
    },
): BookProfitStopLossValidationResult {
    const {
        draft,
        tradePrice,
        tradePriceLabel = "trade price",
        buyPrice,
        maxQuantity,
        priceReferenceLabel = "buy price",
    } = params;
    const priceReference = Number( params.priceReference ?? buyPrice ) || 0;
    const normalizedTradePrice = Number( tradePrice ) || 0;

    // 临界值同时看市价和购买价；默认值、区间和 price 校验只看购买价。
    const bookProfitBlockedLabels = [
        BigMath.gte( normalizedTradePrice, BOOK_PROFIT_PRICE_MAX ) ? tradePriceLabel : null,
        BigMath.gte( priceReference, BOOK_PROFIT_PRICE_MAX ) ? priceReferenceLabel : null,
    ].filter( ( label ): label is string => Boolean( label ) );
    const stopLossBlockedLabels = [
        BigMath.lte( normalizedTradePrice, STOP_LOSS_PRICE_MIN ) ? tradePriceLabel : null,
        BigMath.lte( priceReference, STOP_LOSS_PRICE_MIN ) ? priceReferenceLabel : null,
    ].filter( ( label ): label is string => Boolean( label ) );
    const bookProfitHasPriceRange = hasAvailablePriceRange( "bookProfit", priceReference );
    const stopLossHasPriceRange = hasAvailablePriceRange( "stopLoss", priceReference );
    const bookProfitAvailable = bookProfitBlockedLabels.length === 0 && bookProfitHasPriceRange;
    const stopLossAvailable = stopLossBlockedLabels.length === 0 && stopLossHasPriceRange;
    const bookProfitHelperText = bookProfitAvailable
        ? BOOK_PROFIT_HELPER_TEXT
        : bookProfitBlockedLabels.length > 0
        ? createThresholdDisabledText( "bookProfit", bookProfitBlockedLabels )
        : createRangeUnavailableText( "bookProfit", priceReferenceLabel );
    const stopLossHelperText = stopLossAvailable
        ? STOP_LOSS_HELPER_TEXT
        : stopLossBlockedLabels.length > 0
        ? createThresholdDisabledText( "stopLoss", stopLossBlockedLabels )
        : createRangeUnavailableText( "stopLoss", priceReferenceLabel );

    const bookProfitPrice = parseBookProfitStopLossNumber( draft.bookProfit.price );
    const stopLossPrice = parseBookProfitStopLossNumber( draft.stopLoss.price );
    const bookProfitQuantity = parseBookProfitStopLossNumber( draft.bookProfit.quantity );
    const stopLossQuantity = parseBookProfitStopLossNumber( draft.stopLoss.quantity );
    const bookProfitPriceError = createPriceError( {
        kind: "bookProfit",
        enabled: draft.bookProfit.enabled,
        price: bookProfitPrice,
        priceReference,
        priceReferenceLabel,
    } );
    const stopLossPriceError = createPriceError( {
        kind: "stopLoss",
        enabled: draft.stopLoss.enabled,
        price: stopLossPrice,
        priceReference,
        priceReferenceLabel,
    } );

    const bookProfitValidation = createValidation(
        draft.bookProfit,
        bookProfitHelperText,
        bookProfitAvailable,
        // 自动关闭只留给阈值失效场景。
        // 普通表单错误要在开关保持开启时继续可见、可修改。
        draft.bookProfit.enabled && !bookProfitAvailable,
        draft.bookProfit.enabled ? quantityLimitError( bookProfitQuantity, maxQuantity ) : null,
        bookProfitPriceError,
    );

    const stopLossValidation = createValidation(
        draft.stopLoss,
        stopLossHelperText,
        stopLossAvailable,
        // 自动关闭只留给阈值失效场景。
        // 普通表单错误要在开关保持开启时继续可见、可修改。
        draft.stopLoss.enabled && !stopLossAvailable,
        draft.stopLoss.enabled ? quantityLimitError( stopLossQuantity, maxQuantity ) : null,
        stopLossPriceError,
    );

    return {
        tradePrice,
        buyPrice: priceReference,
        maxQuantity,
        hasBlockingError: !bookProfitValidation.isValid || !stopLossValidation.isValid,
        bookProfit: bookProfitValidation,
        stopLoss: stopLossValidation,
    };
}
